import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { fetchAccessibleGroups } from "../../../../store/features/groupsApi";
import {
  fetchPersonalTicketFromEmailStatus,
  updateTicketFromEmail,
  clearTicketFromEmailError,
} from "../../../../store/features/ticketFromEmail";
import { updateUser } from "../../../../store/features/loginUser";
import SettingsContent from "../shared/SettingsContent";
import SettingsPageHeader from "../shared/SettingsPageHeader";
import BusinessEmailGroupSelect from "../Admin/BusinessEmail/BusinessEmailGroupSelect";
import LinkEmailInfoCard from "./LinkEmailInfoCard";
import LinkEmailRequirements from "./LinkEmailRequirements";
import LinkEmailSuccessCard from "./LinkEmailSuccessCard";
import { getErrorMessage } from "./constants";
import { getActiveGroups, getDefaultGroupId } from "../shared/groupUtils";

function LinkEmailPage() {
  const dispatch = useDispatch();
  const userData = useSelector((state) => state.loginToken);
  const { list: groups, listLoading: groupsLoading } = useSelector(
    (state) => state.peopleGroups,
  );
  const {
    ticket_from_email: groupEmail,
    linked,
    group_id: loadedGroupId,
    fetchLoading,
    loading: saveLoading,
    error: personalEmailError,
    loaded: personalEmailLoaded,
  } = useSelector((state) => state.ticketFromEmail);

  const user = userData?.response?.user;
  const displayName = user?.full_name || "Your name";

  const [selectedGroupId, setSelectedGroupId] = useState("");
  const [emailInput, setEmailInput] = useState("");

  const activeGroups = useMemo(() => getActiveGroups(groups), [groups]);
  const defaultGroupId = useMemo(() => getDefaultGroupId(groups), [groups]);
  const resolvedGroupId = selectedGroupId || defaultGroupId;

  const isCurrentGroupLoaded =
    personalEmailLoaded && String(loadedGroupId) === String(resolvedGroupId);
  const isGroupLinked =
    isCurrentGroupLoaded && linked && Boolean(groupEmail?.trim());

  const loadPersonalEmailStatus = useCallback(
    (groupId) => {
      if (!groupId) return;
      dispatch(fetchPersonalTicketFromEmailStatus({ group_id: groupId }));
    },
    [dispatch],
  );

  useEffect(() => {
    dispatch(fetchAccessibleGroups());
  }, [dispatch]);

  useEffect(() => {
    if (resolvedGroupId) {
      loadPersonalEmailStatus(resolvedGroupId);
    }
  }, [resolvedGroupId, loadPersonalEmailStatus]);

  useEffect(() => {
    if (!isCurrentGroupLoaded) return;
    setEmailInput(groupEmail || "");
  }, [isCurrentGroupLoaded, groupEmail]);

  useEffect(() => {
    if (personalEmailError) {
      toast.error(getErrorMessage(personalEmailError));
      dispatch(clearTicketFromEmailError());
    }
  }, [personalEmailError, dispatch]);

  const previewText = (email) => {
    const addr = email?.trim() || "you@company.com";
    return `${displayName} — ${addr} via TrustChat`;
  };

  const handleLink = async () => {
    const value = emailInput.trim();
    if (!resolvedGroupId) {
      toast.error("Please select a group.");
      return;
    }
    if (!value) {
      toast.error("Please enter an email address.");
      return;
    }

    const action = await dispatch(
      updateTicketFromEmail({
        group_id: resolvedGroupId,
        ticket_from_email: value,
      }),
    );

    if (updateTicketFromEmail.fulfilled.match(action)) {
      dispatch(updateUser({ user: { ticket_from_email: value } }));
      loadPersonalEmailStatus(resolvedGroupId);
      toast.success("Your email has been linked.");
    }
  };

  const handleUnlink = async () => {
    if (!resolvedGroupId) {
      toast.error("Please select a group.");
      return;
    }

    const action = await dispatch(
      updateTicketFromEmail({
        group_id: resolvedGroupId,
        ticket_from_email: "",
      }),
    );

    if (updateTicketFromEmail.fulfilled.match(action)) {
      const stillLinked = Boolean(action.payload?.data?.linked);
      if (!stillLinked) {
        dispatch(updateUser({ user: { ticket_from_email: "" } }));
      }
      setEmailInput("");
      loadPersonalEmailStatus(resolvedGroupId);
      toast.success("Email unlinked.");
    }
  };

  const busy = saveLoading || fetchLoading;

  return (
    <SettingsContent>
      <SettingsPageHeader
        title="Link Email"
        description="Connect your personal company email for outbound ticket replies"
      />

      <BusinessEmailGroupSelect
        groups={activeGroups}
        value={resolvedGroupId}
        onChange={setSelectedGroupId}
        loading={groupsLoading}
      />

      <LinkEmailInfoCard
        previewText={previewText(isGroupLinked ? groupEmail : emailInput)}
        emailInput={emailInput}
        onEmailChange={setEmailInput}
        onLink={handleLink}
        linkDisabled={busy}
        inputDisabled={busy}
        saveLoading={saveLoading}
        showInput={!isGroupLinked}
      />

      {isGroupLinked ? (
        <LinkEmailSuccessCard
          userTicketEmail={groupEmail}
          onUnlink={handleUnlink}
          saveLoading={saveLoading}
        />
      ) : null}

      <LinkEmailRequirements />
    </SettingsContent>
  );
}

export default LinkEmailPage;
